import { sha256Hex, hmacSha256Hex, timingSafeEqualHex } from './sha256.js';

/**
 * SERIALIZACION CANONICA DETERMINISTA.
 *
 * Es la base de tres cosas que no pueden fallar:
 *  - la idempotencia de la cola (`input_hash`),
 *  - la clave de idempotencia del autoguardado,
 *  - el HMAC del `order_snapshot` (DEC-11).
 *
 * `JSON.stringify` NO sirve: el orden de las claves depende del orden de
 * insercion, y `-0`, `1e21` o `1.0000000000000002` producen cadenas distintas
 * para valores equivalentes.
 *
 * Contrato explicito:
 *  1. Las claves de objeto se ordenan por punto de codigo (UTF-16), como `Array#sort`.
 *  2. Los arrays MANTIENEN su orden (es informacion: `z`, `glyphs`, `layers`).
 *  3. `undefined` y las funciones se omiten; en un array se serializan como `null`.
 *  4. Los numeros se normalizan: `-0` -> `0`; se redondea a NORMALIZE_DECIMALS
 *     decimales. Dos documentos que difieren por debajo de 1e-9 mm tienen el
 *     MISMO hash, y eso es deliberado: 1e-9 mm es un nanometro.
 *  5. `NaN` e `Infinity` son un ERROR, no `null` en silencio.
 */

export const NORMALIZE_DECIMALS = 9;

export function normalizeNumber(n: number): number {
  if (!Number.isFinite(n)) {
    throw new Error(`serializacion canonica: numero no finito (${String(n)})`);
  }
  if (n === 0) return 0; // colapsa -0
  if (Math.abs(n) >= 1e15) return n; // fuera del rango donde toFixed es fiable
  const r = Number(n.toFixed(NORMALIZE_DECIMALS));
  return r === 0 ? 0 : r;
}

function serializeValue(value: unknown, path: string): string | undefined {
  if (value === null) return 'null';

  switch (typeof value) {
    case 'number':
      return String(normalizeNumber(value));
    case 'string':
      return JSON.stringify(value);
    case 'boolean':
      return value ? 'true' : 'false';
    case 'undefined':
    case 'function':
      return undefined;
    case 'bigint':
      throw new Error(`serializacion canonica: bigint no admitido en ${path}`);
    case 'symbol':
      throw new Error(`serializacion canonica: symbol no admitido en ${path}`);
  }

  if (Array.isArray(value)) {
    const parts = value.map((v, i) => serializeValue(v, `${path}[${i}]`) ?? 'null');
    return `[${parts.join(',')}]`;
  }

  if (value instanceof Date) {
    return JSON.stringify(value.toISOString());
  }

  const obj = value as Record<string, unknown>;
  const keys = Object.keys(obj).sort();
  const parts: string[] = [];
  for (const key of keys) {
    const serialized = serializeValue(obj[key], `${path}.${key}`);
    if (serialized === undefined) continue; // se omite, no se emite `null`
    parts.push(`${JSON.stringify(key)}:${serialized}`);
  }
  return `{${parts.join(',')}}`;
}

/** Cadena canonica de un valor. Misma entrada semantica -> misma cadena, siempre. */
export function canonicalize(value: unknown): string {
  const out = serializeValue(value, '$');
  if (out === undefined) throw new Error('serializacion canonica: valor raiz vacio');
  return out;
}

/** `sha256:<hex>` del valor canonicalizado. */
export function canonicalHash(value: unknown): string {
  return `sha256:${sha256Hex(canonicalize(value))}`;
}

/** Hash sin prefijo, para incrustar en claves de objeto o nombres de fichero. */
export function canonicalHashHex(value: unknown): string {
  return sha256Hex(canonicalize(value));
}

export interface Signature {
  algo: 'HMAC-SHA256';
  keyId: string;
  value: string;
}

/**
 * DEC-11: un hash recalculable no impide nada. La inmutabilidad del snapshot
 * necesita HMAC con clave de servidor, registrando `key_id` y algoritmo para
 * que un snapshot antiguo se siga pudiendo verificar tras rotar la clave.
 */
export function signCanonical(value: unknown, keyId: string, key: string): Signature {
  return { algo: 'HMAC-SHA256', keyId, value: hmacSha256Hex(key, canonicalize(value)) };
}

export function verifyCanonical(value: unknown, signature: Signature, key: string): boolean {
  if (signature.algo !== 'HMAC-SHA256') return false;
  return timingSafeEqualHex(signature.value, hmacSha256Hex(key, canonicalize(value)));
}
